import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { shopApi, INR } from '../lib/shop';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import '../styles/shop.css';

const Shell = ({ children }) => (
  <div className="si-page"><ShopNavbar /><div className="max-w-3xl mx-auto px-3 md:px-6 py-4 md:py-8">{children}</div><ShopFooter /></div>
);

const Row = ({ label, value, className = '' }) => (
  <div className={`flex justify-between ${className}`}><span>{label}</span><span>{value}</span></div>
);

const OrderConfirmation = () => {
  const { id } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => { shopApi.getOrder(id).then(setO).catch(() => setO({ error: true })); }, [id]);

  if (!o) return <Shell><p className="si-card p-10 text-center">Loading…</p></Shell>;
  if (o.error || !o.id) return <Shell><p className="si-card p-10 text-center text-xl font-bold" data-testid="order-not-found">Order not found.</p></Shell>;
  const a = o.shipping_address || {};

  return (
    <Shell>
      <div className="si-card p-6 md:p-8 text-center" data-testid="order-success">
        <CheckCircle2 className="w-14 h-14 text-[var(--si-green)] mx-auto" />
        <h1 className="mt-3 text-2xl md:text-3xl font-bold">Thank you! Your order is confirmed.</h1>
        <p className="mt-2 text-[var(--si-grey)]">We'll send shipping updates to <strong className="text-[var(--si-black)]">{o.customer_email}</strong>.</p>
      </div>
      <div className="si-card mt-4" data-testid="order-summary">
        <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-[var(--si-line)] text-sm">
          <div><p className="text-[var(--si-grey)]">Order ID</p><p className="font-mono font-semibold" data-testid="order-id">{o.id.slice(0, 12)}</p></div>
          <div><p className="text-[var(--si-grey)]">Status</p><p className="font-semibold capitalize" data-testid="order-status">{(o.status || '').replaceAll('_', ' ')}</p></div>
          <div><p className="text-[var(--si-grey)]">Payment</p><p className="font-semibold capitalize" data-testid="order-payment-status">{o.payment_status}</p></div>
        </div>
        <div className="px-5 divide-y divide-[var(--si-line)]">
          {o.items?.map((it) => (
            <div key={`${it.product_slug}-${it.size}-${it.color}`} className="py-3 flex justify-between gap-3 text-[15px]">
              <span>{it.name} <span className="text-[var(--si-grey)]">{[it.size, it.color].filter(Boolean).join(' · ')} × {it.qty}</span></span>
              <span className="font-semibold">{INR(it.line_total)}</span>
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-[var(--si-line)] space-y-2 text-[15px]">
          <Row label="Subtotal" value={INR(o.subtotal)} />
          {(o.offers || []).map((of) => <Row key={of.offer} label={`${of.label} (${of.free_items} free)`} value={`− ${INR(of.amount)}`} className="si-off" />)}
          {o.discount_amount > 0 && <Row label={`Discount (${o.discount_code})`} value={`− ${INR(o.discount_amount)}`} className="si-off" />}
          <Row label="Delivery" value={o.shipping > 0 ? INR(o.shipping) : 'FREE'} />
          <Row label="Total paid" value={INR(o.total)} className="text-lg font-bold pt-2 border-t border-dashed border-[#C7CCD6]" />
        </div>
        <div className="px-5 py-4 border-t border-[var(--si-line)] text-[15px]">
          <p className="text-sm font-bold text-[var(--si-grey)] mb-1">Delivering to</p>
          <p>{o.customer_name}<br />{a.line1}{a.line2 ? `, ${a.line2}` : ''}<br />{a.city}, {a.state} {a.pincode}<br />{a.country}</p>
        </div>
      </div>
      <div className="mt-6 flex justify-center gap-3">
        <Link to="/shop" className="si-btn si-btn-primary" data-testid="order-continue-shopping">Continue Shopping</Link>
        <Link to="/account" className="si-btn si-btn-outline" data-testid="order-view-orders">My Orders</Link>
      </div>
    </Shell>
  );
};

export default OrderConfirmation;
